import { useCallback, useState } from 'react';

const STORAGE_KEY = 'chat.guildSections.expanded';

/**
 * Only EXPLICIT user choices are stored. A guild the user has never toggled is
 * absent from the record, which lets the caller apply its own default (the chat
 * list defaults the guild containing the active channel to open). Storing a flat
 * list of expanded ids instead would make "never touched" and "deliberately
 * collapsed" indistinguishable, so a user who collapsed their active guild would
 * see it spring back open on every reload.
 *
 * Keys for guilds the user has since left are never pruned — accepted unbounded
 * growth for a per-browser UI pref (a handful of booleans; pruning safely would
 * need the caller's guild list, which is empty while still loading).
 */
type ExpansionOverrides = Record<string, boolean>;

function readStored(): ExpansionOverrides {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const result: ExpansionOverrides = {};
    for (const [guildId, value] of Object.entries(parsed)) {
      if (typeof value === 'boolean') result[guildId] = value;
    }
    return result;
  } catch {
    // Corrupt JSON or localStorage unavailable (private mode, disabled storage).
    return {};
  }
}

function writeStored(overrides: ExpansionOverrides): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
  } catch {
    // Quota exceeded or storage disabled — expansion still works for this
    // session, it just won't survive a reload. Never break the list over it.
  }
}

export interface UseExpandedGuildsReturn {
  /** `undefined` when the user has never toggled this guild — apply your default. */
  isExpanded: (guildId: string) => boolean | undefined;
  setExpanded: (guildId: string, expanded: boolean) => void;
}

/** Remembers which guild sections the user opened, per browser. */
export function useExpandedGuilds(): UseExpandedGuildsReturn {
  const [overrides, setOverrides] = useState<ExpansionOverrides>(readStored);

  const isExpanded = useCallback((guildId: string) => overrides[guildId], [overrides]);

  const setExpanded = useCallback((guildId: string, expanded: boolean) => {
    setOverrides((prev) => {
      const next = { ...prev, [guildId]: expanded };
      writeStored(next);
      return next;
    });
  }, []);

  return { isExpanded, setExpanded };
}
